import { css } from "@emotion/react";
import { Theme, useTheme } from "@mui/material";
import { ReactNode } from "react";
import { match } from "ts-pattern";

import { formatDateTime } from "~/lib/graphql/graphqlClient";

type GenericLimitedContent = {
  __typename: "FanLimitedContent" | "MembershipLimitedContent";
  id: number;
  title: string;
  isEligible: (ids: number[]) => boolean;
  createdAt: string;
  requiredDialogBody: ReactNode;
};

type FanLimitedContent = GenericLimitedContent & {
  __typename: "FanLimitedContent";
  fussyAttestations: {
    id: number;
    fussyAttestationEvent: {
      title: string;
    };
  }[];
};

type MembershipLimitedContent = GenericLimitedContent & {
  __typename: "MembershipLimitedContent";
  visiblePartnerCategoryMemberships: {
    id: number;
    title: string;
  }[];
};

type LimitedContent = FanLimitedContent | MembershipLimitedContent;

const GenericLimitedContentArea = ({
  limitedContent,
}: {
  limitedContent: GenericLimitedContent;
}) => {
  return (
    <div css={limitedContentAreaStyle}>
      <p id="limited-content-title">{limitedContent.title}</p>
      <div id="limited-content-created-at-container">
        <p id="limited-content">
          <span id="limited-content-created-at-label">公開日:</span>
          <span id="limited-content-created-at">
            {formatDateTime(limitedContent.createdAt)}
          </span>
        </p>
      </div>
    </div>
  );
};

const limitedContentAreaStyle = css`
  #limited-content-title {
    font-weight: 600;
  }

  #limited-content-created-at-container {
    display: flex;
    flex-direction: row;
    justify-content: end;
  }

  #limited-content {
    margin-right: 0.5rem;
  }

  #limited-content-created-at-label {
    margin-right: 0.5rem;
  }

  #limited-content-created-at {
    font-size: 0.8rem;
  }
`;

const FanLimitedContentArea = ({
  fanLimitedContent,
}: {
  fanLimitedContent: FanLimitedContent;
}) => {
  return (
    <div>
      <GenericLimitedContentArea limitedContent={fanLimitedContent} />
      <div>
        <span css={labelLimitedToStyle}>Limited to:</span>
        {fanLimitedContent.fussyAttestations.map((a, idx) => (
          <span key={idx} css={labelTitleStyle}>
            {a.fussyAttestationEvent.title}
          </span>
        ))}
      </div>
    </div>
  );
};

const MembershipLimitedContentArea = ({
  membershipLimitedContent,
}: {
  membershipLimitedContent: MembershipLimitedContent;
}) => {
  return (
    <div>
      <GenericLimitedContentArea limitedContent={membershipLimitedContent} />
      <div>
        <span css={labelLimitedToStyle}>Limited to:</span>
        {membershipLimitedContent.visiblePartnerCategoryMemberships.map(
          (m, idx) => (
            <span key={idx} css={labelTitleStyle}>
              {m.title}
            </span>
          )
        )}
      </div>
    </div>
  );
};

const labelLimitedToStyle = css`
  font-size: 0.8rem;
  margin-right: 0.5rem;
`;

const labelTitleStyle = css`
  font-size: 0.8rem;
  margin-right: 0.5rem;
`;

const LimitedContentArea = ({
  limitedContent,
  handleLimitedContentClick,
}: {
  limitedContent: LimitedContent;
  handleLimitedContentClick: (limitedContent: LimitedContent) => void;
}) => {
  const theme = useTheme();
  return (
    <div
      css={limitedContentContainerStyle(theme)}
      onClick={() => handleLimitedContentClick(limitedContent)}
    >
      {match(limitedContent)
        .with({ __typename: "FanLimitedContent" }, (fanLimitedContent) => (
          <FanLimitedContentArea fanLimitedContent={fanLimitedContent} />
        ))
        .with(
          { __typename: "MembershipLimitedContent" },
          (membershipLimitedContent) => (
            <MembershipLimitedContentArea
              membershipLimitedContent={membershipLimitedContent}
            />
          )
        )
        .exhaustive()}
    </div>
  );
};

const limitedContentContainerStyle = (theme: Theme) => css`
  flex: 1;
  padding: 1rem;
  border: 1px solid ${theme.palette.divider};
  border-radius: 0.5rem;
  margin: 0.5rem 0;
  cursor: pointer;
  max-width: 640px;
`;

export type { LimitedContent, FanLimitedContent, MembershipLimitedContent };
export default LimitedContentArea;
